import {
  SelectProps as AntSelectProps,
  BaseOptionType,
  DefaultOptionType,
} from "antd/es/select";
import React, { useEffect, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import FlatList from "common/Lists/FlatList";
import Flex from "common/Boxes/Flex";
import { toggleArrayItem } from "@shared/utils/arrays";
import { useWindowSize } from "react-use";
import {
  useTelegramSafeAreaInsets,
  useVirtualKeyboard,
} from "utils/hooks/misc";
import BottomDrawer from "common/Drawers/BottomDrawer";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { Check } from "common/Icons/Icons";
import { useSelectSearch } from "common/Select/hooks";

interface MobileSelectProps<
  ValueType = any,
  OptionType extends BaseOptionType = DefaultOptionType
> extends AntSelectProps<ValueType, OptionType> {
  mobileTitle?: string;
  bottomExtraElement?: JSX.Element;
}

const MobileSelect = <
  ValueType = any,
  OptionType extends BaseOptionType = DefaultOptionType
>({
  options,
  value,
  open,
  onDropdownVisibleChange,
  onChange,
  mode,
  mobileTitle,
  showSearch: propsShowSearch,
  filterOption = true,
  optionFilterProp,
  bottomExtraElement,
}: MobileSelectProps<ValueType, OptionType>) => {
  const theme = useTheme();
  const showSearch = propsShowSearch ?? (options?.length || 0) > 10;

  const { height } = useWindowSize();
  const { keyboardHeight } = useVirtualKeyboard();
  const { safeAreaInset } = useTelegramSafeAreaInsets();
  const {
    setSearchValue,
    setIsSearch,
    searchComponent,
    searchValue,
    isSearch,
  } = useSelectSearch();

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setIsSearch(false);
    }
  }, [open]);

  const valuesSet = useMemo(() => {
    if (!value) return new Set();
    return new Set(Array.isArray(value) ? value : [value]);
  }, [value]);

  const onOptionClick = (item: OptionType) => {
    const isMultiple =
      (mode === "multiple" || mode === "tags") && Array.isArray(value);
    const updatedValue = isMultiple
      ? toggleArrayItem(value ?? [], item.value)
      : item.value;
    onChange?.(updatedValue, item);
    if (!isMultiple) {
      closeSelect();
    }
  };

  const closeSelect = () => onDropdownVisibleChange?.(false);

  const renderOption = ({ item }: { item: OptionType }) => {
    const selected = valuesSet.has(item.value);
    const isGroup = !!item.options;
    return (
      <Flex
        style={{ width: "100%", overflow: "hidden", minHeight: 22 }}
        onClick={isGroup ? undefined : () => onOptionClick(item)}
      >
        <div style={{ flex: 1, overflow: "hidden" }}>
          {item.children ?? (
            <Text
              as="div"
              type={isGroup ? "secondary" : "primary"}
              $size={typography.fontSizes[isGroup ? "S" : "M"]}
            >
              {item.label}
            </Text>
          )}
        </div>
        {selected && (
          <Check
            style={{
              color: theme.colors.primaryBlue,
              paddingLeft: 12,
              height: 22,
              width: 28,
            }}
          />
        )}
      </Flex>
    );
  };

  const filterOptionBySearch = (option: OptionType) => {
    if (typeof filterOption === "function") {
      return filterOption(searchValue || "", option);
    } else if (filterOption === true) {
      const optionSearchValue: any = option[optionFilterProp ?? "label"];
      return (
        !!optionSearchValue &&
        typeof optionSearchValue === "string" &&
        optionSearchValue
          ?.toLowerCase()
          .includes(searchValue?.toLowerCase() || "")
      );
    }
  };

  const ungroupedOptions = useMemo(() => {
    return options?.reduce((filteredOptions: OptionType[], option) => {
      filteredOptions.push(option);
      if (option.options) {
        filteredOptions = filteredOptions.concat(option.options);
      }
      return filteredOptions;
    }, []);
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!isSearch || !searchValue) return ungroupedOptions;
    return ungroupedOptions?.reduce((filteredOptions: OptionType[], option) => {
      if (option.options) {
        const groupOption = {
          ...option,
          options: option.options.filter(filterOptionBySearch),
        };
        if (groupOption.options?.length) {
          filteredOptions.push(groupOption);
        }
      } else if (filterOptionBySearch(option)) {
        filteredOptions.push(option);
      }
      return filteredOptions;
    }, []);
  }, [ungroupedOptions, searchValue, isSearch]);

  const content = (
    <FlatListWrapper>
      <FlatList
        data={filteredOptions || []}
        renderContent={renderOption}
        wrapperStyle={{
          maxHeight: height * 0.4 + keyboardHeight + safeAreaInset.bottom,
        }}
      />
    </FlatListWrapper>
  );

  return (
    <BottomDrawer
      title={mobileTitle}
      rightButtons={showSearch ? searchComponent : undefined}
      content={content}
      open={open}
      onClose={closeSelect}
      contentStyle={{ padding: 0 }}
      footer={bottomExtraElement}
    />
  );
};

export default MobileSelect;

const FlatListWrapper = styled.div`
  width: 100%;
  flex: 1;
  overflow: hidden;
`;
